// src/context/AuthContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged,
  signOut
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userData, setUserData] = useState(null); // store the full Firestore doc
  const [loading, setLoading] = useState(true);

  // Listen to authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setLoading(false);

      if (user) {
        try {
          // Fetch the user doc from Firestore
          const docRef = doc(db, 'users', user.uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            // If doc has a "role" field (e.g. "artist", "engineer"), store it
            setUserRole(data.role || null);
            // Store the entire user doc, including first/last name, etc.
            setUserData(data);
          } else {
            // No doc found
            setUserRole(null);
            setUserData(null);
          }
        } catch (err) {
          console.error('Error fetching user doc:', err);
          setUserRole(null);
          setUserData(null);
        }
      } else {
        // Logged out
        setUserRole(null);
        setUserData(null);
      }
    });

    return () => unsubscribe();
  }, []);

  // Email/password Signup
  function signup(email, password) {
    return createUserWithEmailAndPassword(auth, email, password);
  }

  // Email/password Login
  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  // Google Sign In (prompt user to pick an account every time)
  function loginWithGoogle() {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    return signInWithPopup(auth, provider);
  }

  // Logout
  function logout() {
    return signOut(auth);
  }

  const value = {
    currentUser,
    userRole,  // e.g., "artist", "engineer"
    userData,  // entire doc with name fields, etc.
    signup,
    login,
    loginWithGoogle,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

// Export a hook for easy usage
export function useAuth() {
  return useContext(AuthContext);
}
